import * as PIXI from "pixi.js";
import gsap from "gsap";
import { Signal } from "signals";
import ServiceLocator from "../../ServiceLocator";
import Card, { card_color_type, card_value_type } from "./Card";
import Player, { game_side_type, ICardLayout } from "./Player";
import { IGameLayers } from "../GameplayScreen";
import ComicBubble from "./ComicBubble";
import Utils from "../../Utils";


export default class DeckEntryAnimation
{
    private _isFirstRound: boolean = null;
    private _deckWrapper: PIXI.Container = null;
    private _arrDeckCard: Array<Card> = null;
    private _leadCard: Card = null;
    private _deckShadow: PIXI.Container = null;
    private _arrPlayer: Array<Player> = null;
    private _tentacle: PIXI.AnimatedSprite = null;
    private _compass: PIXI.AnimatedSprite = null;
    private _playOrderArrow: PIXI.Sprite = null;
    private _gameLayers: IGameLayers = null;

    private _arrPlayerGlobalHandLayout: Array<Array<ICardLayout>> = null;
    private _arrPlayerHand: Array<Array<Card>> = null;

    private _tl: gsap.core.Timeline = null;

    private _onCompleted: Signal = null;

    private _kStartingHandSize: number = 7;


    public set isFirstRound( value: boolean ) { this._isFirstRound = value; }
    public set deckWrapper( value: PIXI.Container ) { this._deckWrapper = value; }
    public set deck( value: Array<Card> ) { this._arrDeckCard = value; }
    public set leadCard( value: Card ) { this._leadCard = value; }
    public set deckShadow( value: PIXI.Container ) { this._deckShadow = value; }
    public set players( value: Array<Player> ) { this._arrPlayer = value; }
    public set tentacle( value: PIXI.AnimatedSprite ) { this._tentacle = value; }
    public set compass( value: PIXI.AnimatedSprite ) { this._compass = value; }
    public set playOrderArrow( value: PIXI.Sprite ) { this._playOrderArrow = value; }
    public set gameLayers( value: IGameLayers ) { this._gameLayers = value; }

    public get onCompleted(): Signal { return this._onCompleted; }


    public constructor() {}

    public init(): void
    {
        console.assert( this._isFirstRound !== null, "DealCardsAnimation.ts :: init() :: this._isFirstRound cannot be null." );
        console.assert( this._deckWrapper != null, "DealCardsAnimation.ts :: init() :: this._deckWrapper cannot be null." );
        console.assert( this._arrDeckCard != null, "DealCardsAnimation.ts :: init() :: this._arrDeckCard cannot be null." );
        console.assert( this._leadCard != null, "DealCardsAnimation.ts :: init() :: this._leadCard cannot be null." );
        console.assert( this._deckShadow != null, "DealCardsAnimation.ts :: init() :: this._deckShadow cannot be null." );
        console.assert( this._tentacle != null, "DealCardsAnimation.ts :: init() :: this._tentacle cannot be null." );
        console.assert( this._compass != null, "DealCardsAnimation.ts :: init() :: this._compass cannot be null." );
        console.assert( this._playOrderArrow != null, "DealCardsAnimation.ts :: init() :: this._playOrderArrow cannot be null." );
        console.assert( this._arrPlayer != null, "DealCardsAnimation.ts :: init() :: this._players cannot be null." );
        console.assert( this._gameLayers != null, "DealCardsAnimation.ts :: init() :: this._gameLayers cannot be null." );

        this._onCompleted = new Signal();

        this._arrPlayerGlobalHandLayout = [];
        this._arrPlayerHand = [];
        for ( let player of this._arrPlayer )
        {
            this._arrPlayerGlobalHandLayout.push( player.findGlobalHandLayout( this._kStartingHandSize ) );
            this._arrPlayerHand.push( [] );
        }
    }

    public end(): void
    {
        this._onCompleted.dispose();
        this._onCompleted = null;

        if ( this._tl )
        {
            this._tl.kill();
            this._tl = null;
        }
    }

    public play(): void
    {
        this._tl = new gsap.core.Timeline();

        if ( this._isFirstRound )
        {
            this._tentacle.visible = false;
            this._compass.visible = false;
        }
        this._playOrderArrow.visible = false;

        for ( let i: number = 0; i < this._arrPlayer.length; ++i )
        {
            let player: Player = this._arrPlayer[ i ];

            player.onDeckEntryAnimationStarted( this._isFirstRound );

            for ( let j: number = 0; j < this._kStartingHandSize; ++j )
            {
                let card: Card = this._arrDeckCard.pop();
                // TEST:
                /*card.value = card_value_type.EIGHT;
                card.color = card_color_type.ANY;//Math.floor( Math.random() * 4 );
                card.setColor( card.color);*/
                //
                player.addCard( card );
            }
            
            if ( player.side == game_side_type.SOUTH )
            {
                player.sortHandFaceUp();
            }
            else
            {
                player.sortHandFaceDown();
            }

            let cardIndex: number = 0;
            let arrTempCard: Array<Card> = [];
            for ( let card of player.hand )
            {
                let isLastIndex: boolean = true;
                for ( let j: number = 0; j < arrTempCard.length; ++j )
                {
                    if ( card.parent.angle < arrTempCard[ j ].parent.angle )
                    {
                        arrTempCard.splice( j, 0, card );
                        isLastIndex = false;
                        break;
                    }
                }
                if ( isLastIndex )
                {
                    arrTempCard.push( card );
                }
            }
            for ( let card of arrTempCard )
            {
                card.y += 400;
                //this._tl.from( card, { alpha: 0, duration: 0.1, ease: "sine.out" }, 1 + cardIndex * 0.2 );
                this._tl.to( card, { y: "-=400", duration: 0.4, ease: "sine.out" }, 1 + cardIndex * 0.16 );
                
                cardIndex += 1;
            }
        }
        
        for ( let card of this._arrDeckCard )
        {
            let cardShadow: PIXI.Sprite = new PIXI.Sprite( ServiceLocator.resources.deck_shadow.texture );
            cardShadow.anchor.set( 0.5 );
            cardShadow.position.copyFrom( card.position );
            cardShadow.x -= 10;
            cardShadow.y -= 10;
            this._deckShadow.addChild( cardShadow );
        }

        this._tl.from( this._deckWrapper, { x: -ServiceLocator.app.safeAreaMarginX - 100, duration: 0.6, ease: "sine.out" }, 0 );
        this._tl.from( this._deckShadow, { x: -ServiceLocator.app.safeAreaMarginX - 100, duration: 0.6, ease: "sine.out" }, 0 );
        this._tl.call( () => { 
            this._leadCard.visible = true; 
            this._leadCard.shadow.visible = true;
        }, null, 0.8 );
        this._tl.to( this._leadCard, { x: 412, duration: 0.24, ease: "sine.out" }, 0.8 );
        this._tl.to( this._leadCard.shadow, { x: 412 - 10, duration: 0.24, ease: "sine.out" }, 0.8 );

        let delay: number = 0;
        if ( this._isFirstRound )
        {
            let tempPlayers: Array<Player> = Utils.softShuffle( this._arrPlayer );
            for ( let player of tempPlayers )
            {
                this._tl.to( player.portraitWrapper.scale, { x: 1, y: 1, duration: 0.4, ease: "back.out" }, 2.4 + delay );

                delay += 0.2;
            }
        
            this._tl.call( () => { 
                this._tentacle.visible = true;
                this._compass.visible = true; 

                this._tentacle.gotoAndPlay( 0 );
            }, null, 2.8 + delay );
            this._tl.from( this._tentacle, { x: "-=200", y: "-=200", duration: 0.8, ease: "sine.out" }, 2.8 + delay );
            this._tl.from( this._compass, { x: "+=200", y: "+=200", duration: 0.8, ease: "sine.out" }, 2.8 + delay );
            this._tl.call( () => { this._compass.gotoAndPlay( 0 ); }, null, 3.6 + delay );
        }

        this._tl.call( () => {  
            for ( let player of this._arrPlayer )
            {
                player.onDeckEntryAnimationEnded();
            }
            this._playOrderArrow.visible = true;

            this._onCompleted.dispatch(); 
        }, null );
        //
        ServiceLocator.game.tweenGroup.add( this._tl );

        ServiceLocator.audio.playSfx( "slide_" + Utils.randomInteger( 0, 3 ).toString() );

        console.log( "PLAY!" );
    }
}